// @react
import React, { ReactElement } from 'react'
// @types
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
// @components
import Spacer from '@bit/atd.web.spacer'
import QueryError from 'modules/common/components/Errors/QueryError'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import CommunicationForm from 'modules/profile/components/Communication/Form'
import EditAction from '../EditAction'

/**
 * 5.0.4.1 Profile - Communication - Preferences
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3123%3A16072
 *
 *
 * User/communicationPreferences/ATDNewsletters	changeManagement
 * User/communicationPreferences/ATDNewsletters	globalPerspectives
 * User/communicationPreferences/ATDNewsletters	higherEducation
 * User/communicationPreferences/ATDNewsletters	instructionalDesign
 * User/communicationPreferences/ATDNewsletters	leadershipDevelopment
 * User/communicationPreferences/ATDNewsletters	learningTechnologies
 * User/communicationPreferences/ATDNewsletters	managementDevelopment
 * User/communicationPreferences/ATDNewsletters	myCareer
 * User/communicationPreferences/ATDNewsletters	performanceConsulting
 * User/communicationPreferences/ATDNewsletters	salesEnablement
 * User/communicationPreferences/ATDNewsletters	scienceOfLearning
 * User/communicationPreferences/ATDNewsletters	talentManagement
 * User/communicationPreferences/ATDNewsletters	trainingDelivery
 *
 * User/communicationPreferences/ATDMemberBenefitEmails	theBuzz
 * User/communicationPreferences/ATDMemberBenefitEmails	ATDLinks
 * User/communicationPreferences/ATDMemberBenefitEmails	ATDMemberEngagement
 *
 * User/communicationPreferences/industryNewsletters	government
 * User/communicationPreferences/industryNewsletters	healthcare
 * User/communicationPreferences/industryNewsletters	TDExecutive
 *
 * User/communicationPreferences/opportunitiesAndOffers	education
 * User/communicationPreferences/opportunitiesAndOffers	membership
 * User/communicationPreferences/opportunitiesAndOffers	conferencesInUS
 * User/communicationPreferences/opportunitiesAndOffers	publications
 * User/communicationPreferences/opportunitiesAndOffers	webcasts
 * User/communicationPreferences/opportunitiesAndOffers	conferencesEventsOutsideUS
 * User/communicationPreferences/opportunitiesAndOffers	certificationPrograms
 * User/communicationPreferences/opportunitiesAndOffers	researchSurveys
 * User/communicationPreferences/opportunitiesAndOffers	ATDJobBank
 * User/communicationPreferences/opportunitiesAndOffers	recommendedResources
 *
 */

const CommunicationContainer = (): ReactElement => {
  /****************************************
   *  GQL HOOKS
   ****************************************/

  const { loading: isLoading, error: isError, data, refetch } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-first' })

  /****************************************
   *  RENDER
   ****************************************/

  if (isError) {
    return <QueryError />
  }

  if (isLoading || !data) {
    return (
      <>
        <Box
          header={{
            title: 'Choose the e-mail updates you wish to receive:',
            actions: <EditAction disabled />,
          }}
        >
          <SkeletonLoader />
        </Box>

        <Box
          header={{
            title: "If you'd like fewer emails, select an option below:",
          }}
        >
          <SkeletonLoader />
        </Box>

        <Spacer size={24} />
        <Box
          header={{
            title: 'Select your postal mail preferences:',
          }}
        >
          <SkeletonLoader />
        </Box>
      </>
    )
  }

  return <CommunicationForm data={data} refetch={refetch} />
}

export default CommunicationContainer
